import { useState } from "react";
import { Moon, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { summarize, teamLogo } from "@/components/accuracy/AccuracyTools";
import { shortDate } from "@/lib/format";
import "./LastNight.css";

// Finished nights, oldest first.
export const nightDays = (games, today) => [...new Set(games.filter((g) => g.game_day < today).map((g) => g.game_day))].sort();

// One night; saved morning projections win over the backtest when both exist.
export function pickNight(games, day) {
  if (!day) return null;
  const night = games.filter((g) => g.game_day === day);
  const pregame = night.filter((g) => g.source === "pregame");
  const list = pregame.length ? pregame : night;
  return { day, source: pregame.length ? "pregame" : "backtest", games: list, summary: summarize(list) };
}

const r0 = (v) => Math.round(v);

function GameLine({ g }) {
  const off = r0(g.proj_total - g.actual_total);
  const tone = Math.abs(off) <= 6 ? "good" : Math.abs(off) <= 12 ? "warn" : "bad";
  return (
    <li className="ln-game" data-testid={`last-night-game-${g.game_id}`}>
      <span className="ln-teams">
        <img src={teamLogo(g.away.team_code)} alt="" />{g.away.team_code}
        <b>{r0(g.away.actual.pts)}-{r0(g.home.actual.pts)}</b>
        {g.home.team_code}<img src={teamLogo(g.home.team_code)} alt="" />
      </span>
      <span className="ln-proj">proj {r0(g.away.proj.pts)}-{r0(g.home.proj.pts)}</span>
      <span className={`ln-off ${tone}`}>{off > 0 ? "+" : ""}{off}</span>
      <span className={`ln-win ${g.winner_correct ? "ok" : "no"}`} aria-label={g.winner_correct ? "winner right" : "winner wrong"}>
        {g.winner_correct ? <Check size={14} /> : <X size={14} />}
      </span>
    </li>
  );
}

export function LastNightButton({ today }) {
  const [all, setAll] = useState(undefined);
  const [idx, setIdx] = useState(0);
  const load = (open) => {
    if (!open || all !== undefined) return;
    api.get("/rotation/accuracy", { params: { limit: 300 } })
      .then((r) => { const games = r.data.games || []; setAll(games); setIdx(nightDays(games, today).length - 1); })
      .catch(() => setAll(null));
  };
  const days = all ? nightDays(all, today) : [];
  const night = all === undefined ? undefined : pickNight(all || [], days[idx]);
  const s = night?.summary;
  return (
    <Dialog onOpenChange={load}>
      <DialogTrigger asChild>
        <button type="button" className="ln-trigger" data-testid="last-night-button"><Moon size={15} /> Last night</button>
      </DialogTrigger>
      <DialogContent className="ln-dialog" data-testid="last-night-dialog">
        <DialogHeader className="ln-head">
          <DialogTitle className="font-display uppercase tracking-wide">
            {idx === days.length - 1 ? "Last night" : "Earlier night"}
            {night && <span className="ln-day" data-testid="last-night-day">{shortDate(night.day)} · {night.source === "pregame" ? "morning projections" : "backtest"}</span>}
          </DialogTitle>
          {days.length > 0 && (
            <div className="ln-nav">
              <button type="button" aria-label="Previous night" data-testid="last-night-prev" disabled={idx <= 0} onClick={() => setIdx(idx - 1)}><ChevronLeft size={16} /></button>
              <span data-testid="last-night-position">{idx + 1}/{days.length}</span>
              <button type="button" aria-label="Next night" data-testid="last-night-next" disabled={idx >= days.length - 1} onClick={() => setIdx(idx + 1)}><ChevronRight size={16} /></button>
            </div>
          )}
        </DialogHeader>
        {all === undefined && <p className="ln-empty">Loading…</p>}
        {night === null && <p className="ln-empty" data-testid="last-night-empty">No finished games yet.</p>}
        {s && (
          <>
            <div className="ln-stats" data-testid="last-night-summary">
              <div><small>Winner right</small><strong>{night.games.filter((g) => g.winner_correct).length}/{night.games.length}</strong></div>
              <div><small>Avg miss / team</small><strong>{s.mae_pts.toFixed(1)}</strong></div>
              <div><small>Avg miss / total</small><strong>{s.mae_game_total.toFixed(1)}</strong></div>
              <div><small>Bias</small><strong>{s.bias_pts > 0 ? "+" : ""}{s.bias_pts.toFixed(1)}</strong></div>
            </div>
            <ul className="ln-list">{night.games.map((g) => <GameLine key={g.game_id} g={g} />)}</ul>
            {night.source === "backtest" && <p className="ln-note">No saved morning projections for this night — showing the backtest (optimistic).</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
